import type {ReactElement} from 'react';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import type {
  AiGeneratedContent,
  NonMiniAiTextPromptBoxProps,
  PromptBoxAlterationLoadingText,
  PromptBoxConfigOptionType,
  PromptBoxTitles,
  SelectedValueType,
} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiPromptBoxVariant, GENERATION_OPTIONS_COMPONENT_ID, NUMBER_OF_CAPTIONS_PER_GENERATION} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiTextPromptBoxError} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-error';
import {AiTextPromptBoxLoading} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-loading';
import {AiTextListItem} from '@Components/ai-text-prompt-box/components/ai-text-list-item';
import {AiTextSelectedCaptionTextArea} from '@Components/ai-text-prompt-box/components/ai-text-selected-caption-text-area';
import {SkeletonBrick} from '@Components/skeleton-brick';
import usePromptConfigOptions from '@Hooks/ai-prompt-box/usePromptConfigOptions';
import {isRecentAiGeneration} from '@Libraries/ai-text-prompt-box-library';
import styles from './ai-text-prompt-box-content.module.scss';
import type {AiTextPromptBoxSuggestionsProps} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-suggestions';
import {AiTextPromptBoxSuggestions} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-suggestions';

interface AiTextPromptBoxContentProps {
  content: AiGeneratedContent[];
  optionsToUse: PromptBoxConfigOptionType[];
  isInMobileMode?: boolean;
  selectedContent?: AiGeneratedContent | null;
  isHidden?: boolean;
  header?: ReactElement | null;
  onSelectContent: (content: AiGeneratedContent) => void;
  isLoadingGenerations?: boolean;
  isLoadingAlteration?: boolean;
  isLoadingUseage?: boolean;
  isLoadingRecents?: boolean;
  errorMessage?: string | null;
  variant: AiPromptBoxVariant;
  dropdownInUpdatedState?: PromptBoxConfigOptionType[];
  titles?: PromptBoxTitles;
  alterationLoadingTexts?: PromptBoxAlterationLoadingText;
  onPromptSuggestionClicked?: AiTextPromptBoxSuggestionsProps['onOptionSelect'];
  promptSuggestions?: NonMiniAiTextPromptBoxProps['internationalizedPromptSuggestions'];
  prompt?: string;
  className?: string;
  contentClassName?: string;
}

export function AiTextPromptBoxContent({
  isLoadingRecents = false,
  isLoadingAlteration = false,
  isLoadingGenerations = false,
  variant = AiPromptBoxVariant.MONOCONTENT,
  ...props
}: AiTextPromptBoxContentProps): ReactElement | null {
  const [scrollEndVisible, setScrollEndVisible] = useState(false);
  const scrollEndShadowRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {getCachedValues, defaultValues} = usePromptConfigOptions(props.optionsToUse);
  const previouslyUsedOptionsForAlteringCaption: SelectedValueType = getCachedValues() ?? defaultValues;

  const shouldErrorBeInsideTheContentArea = props.isInMobileMode && !!props.selectedContent;
  const isInRecentMode = isRecentAiGeneration(props.content[0]);
  const isInContentListMode = !isInRecentMode && !props.selectedContent && props.content.length > 0;
  const isError = !!props.errorMessage;
  const isMainScreen = !isInRecentMode && !props.selectedContent && props.content.length < 1;

  const onClickContent = (content: AiGeneratedContent): void => {
    props.onSelectContent(content);
  };

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    containerRef.current.scrollTo({top: 0, left: 0, behavior: 'smooth'});
    handleBottomShadow(containerRef.current);
  }, [props.content.length, containerRef, scrollEndShadowRef]);

  const shouldOverflowBeScrollable = useMemo(() => {
    return !!props.content.length && !props.selectedContent;
  }, [props.content, props.selectedContent]);

  const getCaptionListOrSelectedContentInATextArea = (): ReactElement => {
    if (!props.selectedContent) {
      return getRecentOrGeneratedCaptionList();
    }

    return getTextAreaWithSelectedContent();
  };

  const getLoadingMessage = (): string => {
    if (isLoadingRecents) {
      return window.i18next.t('pmwjs_getting_recent_generations');
    }

    return window.i18next.t('pmwjs_thinking_of_ideas');
  };

  const captionListScrollHandler = (event: React.UIEvent<HTMLElement>): void => {
    const target = event.target as HTMLElement;

    if (!target) {
      return;
    }

    handleBottomShadow(target);
  };

  const handleBottomShadow = (target: HTMLElement): void => {
    const scrollEndLinienceOffset = 20;
    const totalScrollHeight = target.scrollHeight;

    const currentScrollPosition = target.scrollTop + target.clientHeight;

    if (scrollEndShadowRef.current && !scrollEndVisible && !props.isInMobileMode) {
      scrollEndShadowRef.current.style.top = `${currentScrollPosition + scrollEndLinienceOffset}px`;
    }

    if (currentScrollPosition + scrollEndLinienceOffset >= totalScrollHeight) {
      setScrollEndVisible(true);
    } else {
      setScrollEndVisible(false);
    }
  };

  const moveCursorToEndOfInput = (e: React.FocusEvent<HTMLTextAreaElement>): void => {
    if (!e.target) {
      return;
    }

    const val = e.target.value;
    e.target.value = '';
    e.target.value = val;
  };

  const getHeaderIfHeaderPropIsPresent = (): ReactElement | null => {
    if (props.header) {
      return props.header;
    }

    return null;
  };

  const getPromptBoxContentClassNames = (): string => {
    return `
        ${styles.aiTextsContentContainer}
        ${props.selectedContent ? styles.captionSelected : ''}
        ${props.isInMobileMode ? styles.mobileMode : ''} 
        ${shouldOverflowBeScrollable ? styles.overflowAuto : styles.overflowVisible}
        ${props.className ?? ''}
      `;
  };

  const getOverflowShadow = (): ReactElement | null => {
    if (!props.content.length) {
      return null;
    }

    if (scrollEndVisible) {
      return null;
    }

    if (props.selectedContent) {
      return null;
    }

    return <div ref={scrollEndShadowRef} className={`${styles.aiTextsListContainerEndShadow} ${isInRecentMode ? styles.recentsMode : ''}`} />;
  };

  const getPreviousCaptionsSeparatorMarkup = (currentIndex: number): ReactElement | null => {
    if (!props.content.length) {
      return null;
    }

    if (isInRecentMode) {
      return null;
    }

    if (currentIndex === NUMBER_OF_CAPTIONS_PER_GENERATION) {
      return (
        <div className={`_fit-width ${styles.previousCaptionSeperator}`}>
          <AiTextPromptBoxLoading hiddenEllipsis message={window.i18next.t('pmwjs_previous_generations')} isShown={true} />
        </div>
      );
    }

    return null;
  };

  const getRecentsSkeleton = (): ReactElement => {
    const numberOfSkeletonItems = 7;
    return (
      <ul className={styles.aiTextsList}>
        {Array.from(Array(numberOfSkeletonItems).keys()).map(() => {
          return (
            <li>
              <SkeletonBrick height={56} radius="8" isPulsating className="spacing-m-b-5" />
            </li>
          );
        })}
      </ul>
    );
  };

  const getCaptionList = (): ReactElement => {
    return (
      <ul className={styles.aiTextsList}>
        {props.content.map((content, index) => {
          return (
            <AiTextListItem
              key={isRecentAiGeneration(content) ? content.id : content.text.toString().trim()}
              previousCaptionsSeparatorMarkup={getPreviousCaptionsSeparatorMarkup(index)}
              areNewCaptionsLoading={false}
              content={content}
              index={index}
              onClick={onClickContent}
              variant={variant}
            />
          );
        })}
      </ul>
    );
  };

  const getRecentOrGeneratedCaptionList = (): ReactElement => {
    if (isInRecentMode && isLoadingRecents) {
      return getRecentsSkeleton();
    }

    return getCaptionList();
  };

  const getTextAreaWithSelectedContent = (): ReactElement => {
    return (
      <AiTextSelectedCaptionTextArea
        key={JSON.stringify(previouslyUsedOptionsForAlteringCaption)}
        titles={props.titles}
        isInMobileMode={props.isInMobileMode}
        isLoading={isLoadingAlteration}
        shouldErrorBeInsideTheContentArea={shouldErrorBeInsideTheContentArea}
        value={props.selectedContent}
        contentOnFocus={moveCursorToEndOfInput}
        contentOnChange={props.onSelectContent}
        dropdownInUpdatedState={props.dropdownInUpdatedState}
        variant={variant}
        optionsToUse={props.optionsToUse}
        id={GENERATION_OPTIONS_COMPONENT_ID}
        alterationLoadingTexts={props.alterationLoadingTexts}
        errorMessage={props.errorMessage}
      />
    );
  };

  const getIsContentAreaFullHeight = (): boolean => {
    return (!!props.selectedContent || !isInContentListMode) && !isError;
  };

  const getPromptBoxContent = (): ReactElement => {
    return (
      <div className={`${getIsContentAreaFullHeight() ? '_full-height' : styles.fixedHeightInLoadingState} ${props.contentClassName ?? ''}`}>
        <AiTextPromptBoxLoading isShown={isLoadingGenerations} message={getLoadingMessage()} containerClasses="flexbox flex-row justify-content-center spacing-m-b-3" />
        <AiTextPromptBoxError isError={isError} errorMessage={props.errorMessage ?? ''} isHidden={shouldErrorBeInsideTheContentArea} />
        <AiTextPromptBoxSuggestions
          suggestions={props.promptSuggestions ?? []}
          onOptionSelect={props.onPromptSuggestionClicked}
          isUnmounted={isLoadingGenerations || !isMainScreen}
          isHidden={AiPromptBoxVariant.MONOCONTENT !== variant || !!props.prompt}
        />
        {getCaptionListOrSelectedContentInATextArea()}
      </div>
    );
  };

  if (props.isHidden) {
    return null;
  }

  return (
    <div ref={containerRef} className={getPromptBoxContentClassNames()} onScroll={captionListScrollHandler}>
      {getHeaderIfHeaderPropIsPresent()}
      {getPromptBoxContent()}
      {getOverflowShadow()}
    </div>
  );
}
